import React, { useEffect, useState } from 'react';
import { submitEvaluatorReport } from '../../services/api';
import useMitiSlotMachine from '../../hooks/useMitiSlotMachine';
import SpecificSkillExpertResponseCard from './SpecificSkillExpertResponseCard';
import ContemplatingIndicator from '../chat/ContemplatingIndicator';
import SkillUsedReasoningCard from './SkillUsedReasoningCard';
import '../dialogs/DialogBase.css';
import '../dialogs/FeedbackDialog.css';
import './ReportOarsPanel.css';

function PatientDescriptionText({ value }) {
  const raw = String(value || '').trim();
  const patientPrefix = '<b>Patient:</b>';

  if (!raw) {
    return null;
  }

  if (raw.startsWith(patientPrefix)) {
    return raw.slice(patientPrefix.length).trim();
  }

  return raw;
}

function OarsFeedbackContext({ oarsAttempt }) {
  return (
    <div className="oars-feedback-context-card">
      <div className="app-feedback-field">
        <p className="app-feedback-question-text">Your Response</p>
        <div className="message-text message-text--user oars-report-response-box">
          <div className="oars-report-response-scroll">
            <p className="oars-report-text">{oarsAttempt?.utterance || '—'}</p>
          </div>
        </div>
      </div>
      <div className="app-feedback-field">
        <p className="app-feedback-question-text">Evaluator Feedback</p>
        <div className="analytics-card oars-report-feedback-card">
          <div className="analytics-card-content oars-report-feedback-content">
            <p className="sr-feedback-text">{oarsAttempt?.evaluation?.feedback || '—'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OarsActionDock({
  show = false,
  t,
  onOarsTryAgain,
  onOarsNextExercise,
  onReturnToAccount,
  isSpecificSkillReplay = false,
  showSpecificSkillsPostAttemptCue = false,
}) {
  if (!show) {
    return null;
  }

  return (
    <div className="oars-action-dock">
      <div className="oars-action-bar">
        <button
          type="button"
          className={`oars-btn oars-btn--secondary${!isSpecificSkillReplay && showSpecificSkillsPostAttemptCue ? ' oars-btn--selection-cue' : ''}`}
          data-guided-highlight-purpose={!isSpecificSkillReplay && showSpecificSkillsPostAttemptCue ? 'Try another response to this same exercise.' : undefined}
          onClick={onOarsTryAgain}
        >
          {t('report_oars_try_again')}
        </button>
        <div className="oars-next-exercise-wrap">
          <button
            type="button"
            className={`oars-btn oars-btn--primary${showSpecificSkillsPostAttemptCue ? ' oars-btn--selection-cue' : ''}`}
            data-guided-highlight-purpose={showSpecificSkillsPostAttemptCue ? 'Continue to a new Specific Skills exercise.' : undefined}
            onClick={isSpecificSkillReplay ? onReturnToAccount : onOarsNextExercise}
          >
            {isSpecificSkillReplay ? t('account_return_to_account') : t('report_oars_next_exercise')}
          </button>
          {!isSpecificSkillReplay && (
            <div className="oars-next-exercise-tooltip" role="status">
              You can return to this exercise in your account page.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportOarsPanel({
  t,
  isOarsMode = false,
  oarsExercise = null,
  oarsAttempt = null,
  oarsAnalyzing = false,
  oarsStreamingEvaluation = null,
  onOarsNextExercise,
  onOarsTryAgain,
  onReturnToAccount,
  isSpecificSkillReplay = false,
  onShowAutomatedMiscMitiCounsellor,
  showSpecificSkillsPostAttemptCue = false,
  notify,
  MitiChipComponent,
  actionDockDetached = false,
  theme = 'dark',
}) {
  const [showGroundTruth, setShowGroundTruth] = useState(false);
  const [oarsAnalyticsVisible, setOarsAnalyticsVisible] = useState(true);
  const [feedbackReportOpen, setFeedbackReportOpen] = useState(false);
  const [feedbackReportText, setFeedbackReportText] = useState('');
  const [feedbackReportSubmitting, setFeedbackReportSubmitting] = useState(false);

  const showOarsStage1ReadyState = isOarsMode && !oarsExercise;
  const showOarsStage2ContextState = isOarsMode && !!oarsExercise && !oarsAttempt && !oarsAnalyzing;
  const showOarsActionDock = isOarsMode && !!oarsExercise && !!oarsAttempt && !oarsAnalyzing;
  const showOarsAnalytics = isOarsMode && oarsExercise && (oarsAttempt || oarsAnalyzing);
  const visiblePredictedLabels = oarsAttempt
    ? (oarsAttempt.predicted_labels?.length ? oarsAttempt.predicted_labels : ['NC'])
    : (oarsStreamingEvaluation?.predicted_labels || []);
  const visibleGuidance = oarsAttempt?.evaluation?.feedback || oarsStreamingEvaluation?.feedback || '';
  const classifierExplanation = oarsAttempt?.classifier_explanation || oarsStreamingEvaluation?.classifier_explanation || '';
  const expertResponseText = oarsExercise?.target_turn?.text || oarsAttempt?.target_turn_text || '';
  const mitiSlotLabel = useMitiSlotMachine(
    oarsAnalyzing && !oarsAttempt && visiblePredictedLabels.length === 0,
  );
  const patientDescriptionText = oarsExercise?.patient_description || '';
  const feedbackReportWordCount = feedbackReportText.trim()
    ? feedbackReportText.trim().split(/\s+/).filter(Boolean).length
    : 0;
  const isComplexReflectionExercise = (
    oarsExercise?.oars_category === 'CR' ||
    oarsAttempt?.oarsCategory === 'CR' ||
    (oarsAttempt?.ground_truth_labels || []).includes('CR') ||
    String(oarsExercise?.id || oarsAttempt?.exercise_id || '').startsWith('CR_')
  );
  useEffect(() => {
    if (!oarsAttempt) {
      setShowGroundTruth(false);
    }
  }, [oarsAttempt, oarsExercise?.id]);

  const closeFeedbackReport = () => {
    setFeedbackReportOpen(false);
    setFeedbackReportText('');
    setFeedbackReportSubmitting(false);
  };

  const handleFeedbackReportSubmit = async () => {
    if (!oarsExercise?.id || feedbackReportSubmitting) {
      return;
    }

    setFeedbackReportSubmitting(true);
    try {
      await submitEvaluatorReport({
        attemptId: oarsAttempt?.attempt_id || oarsAttempt?.id || null,
        exerciseId: oarsAttempt?.exercise_id || oarsExercise.id,
        sessionInput: oarsAttempt?.utterance || '',
        evaluatorFeedback: oarsAttempt?.evaluation?.feedback || '',
        reportText: feedbackReportText.trim(),
      });
      closeFeedbackReport();
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to submit report. Please try again.';
      notify(message);
      setFeedbackReportSubmitting(false);
    }
  };

  return (
    <>
      {showOarsStage1ReadyState && (
        <div className="panel-info">
          <p className="info-text">{t('report_ready_title')}</p>
          <p className="info-subtext">{t('report_empty_no_session')}</p>
        </div>
      )}

      {showOarsStage2ContextState && (
        <div className="oars-context-panel oars-report-context-panel">
          <div className="analytics-header">
            <h4 className="analytics-title">Conversation Context</h4>
            <p className="analytics-subtitle">Background on the client for this exercise</p>
          </div>
          <div className="oars-context-body">
            <div><PatientDescriptionText value={patientDescriptionText} /></div>
          </div>
          <div className="oars-action-bar">
            <div className="oars-next-exercise-wrap">
              <button
                type="button"
                className="oars-btn oars-btn--primary"
                onClick={onOarsNextExercise}
              >
                {t('report_oars_next_exercise')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOarsAnalytics && (
        <div className="oars-analytics-shell">
          <div className="in-session-analytics oars-analytics-content">
            <div className="analytics-header">
              <div className="analytics-header-row">
                <div className="analytics-header-copy">
                  <h4 className="analytics-title">{t('report_oars_title')}</h4>
                </div>
                <div className="analytics-header-right">
                  <button
                    type="button"
                    className="analytics-toggle-chevron"
                    onClick={() => setOarsAnalyticsVisible((value) => !value)}
                    aria-expanded={oarsAnalyticsVisible}
                    aria-label={oarsAnalyticsVisible ? t('report_hide_analytics_aria') : t('report_show_analytics_aria')}
                    title={oarsAnalyticsVisible ? t('report_hide_analytics_aria') : t('report_show_analytics_aria')}
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      {oarsAnalyticsVisible ? <polyline points="6 9 12 15 18 9" /> : <polyline points="6 15 12 9 18 15" />}
                    </svg>
                  </button>
                </div>
              </div>
              {oarsAnalyticsVisible && <div className="analytics-header-divider" aria-hidden="true" />}
            </div>
            {oarsAnalyticsVisible && (
            <div className="analytics-scroll-body">
              <div className="analytics-sections">
              <SkillUsedReasoningCard
                explanation={classifierExplanation}
                codeCount={visiblePredictedLabels.length}
                theme={theme}
              >
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">Skill Used</h5>
                  {onShowAutomatedMiscMitiCounsellor && (
                    <button
                      type="button"
                      className="analytics-toggle-icon"
                      onClick={onShowAutomatedMiscMitiCounsellor}
                      aria-label="Open counsellor coding manual"
                      title="Open counsellor coding manual"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H4z" />
                        <path d="M6.5 3v13" />
                        <path d="M8.5 7h8" />
                        <path d="M8.5 10.5h8" />
                        <path d="M8.5 14h5" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="analytics-card-content">
                  {!oarsAttempt && visiblePredictedLabels.length === 0 ? (
                    oarsAnalyzing ? (
                      <div className="oars-attempt-entry miti-slot-machine" aria-label="Analyzing skill code">
                        <div className="oars-label-row" aria-hidden="true">
                          <MitiChipComponent key={mitiSlotLabel} label={mitiSlotLabel} expand />
                        </div>
                      </div>
                    ) : <p className="analytics-empty">{t('report_oars_empty')}</p>
                  ) : (
                    <div className="oars-attempt-entry">
                      <div className="oars-label-row">
                        {visiblePredictedLabels.map((label) => (
                          <MitiChipComponent key={label} label={label} expand />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </SkillUsedReasoningCard>

              <div className="analytics-card">
                <div className="analytics-card-header">
                  <h5 className="analytics-card-title">Guidance</h5>
                  <button
                    type="button"
                    className="analytics-toggle-icon feedback-report-icon"
                    onClick={() => setFeedbackReportOpen(true)}
                    aria-label="Report incorrect feedback"
                    title={oarsAttempt?.evaluation ? 'Report incorrect feedback' : 'Available when guidance is complete'}
                    disabled={!oarsAttempt?.evaluation}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M4 21V5" />
                      <path d="M4 5h10l-1.4 3L18 11H4" />
                    </svg>
                  </button>
                </div>
                <div className="analytics-card-content">
                  {visibleGuidance
                    ? <p className={`sr-feedback-text${oarsAnalyzing ? ' specific-skill-guidance-streaming' : ''}`}>{visibleGuidance}</p>
                    : <p className="analytics-empty">{oarsAnalyzing ? <ContemplatingIndicator /> : t('report_no_data_yet')}</p>
                  }
                </div>
              </div>

              <SpecificSkillExpertResponseCard
                t={t}
                responseText={expertResponseText}
                revealed={showGroundTruth}
                onToggle={() => setShowGroundTruth((value) => !value)}
                showComplexReflectionHint={isComplexReflectionExercise}
              />
            </div>
            </div>
            )}
          </div>
        </div>
      )}

      {feedbackReportOpen && (
        <div
          className="app-confirm-overlay app-feedback-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="app-confirm-panel app-feedback-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="app-feedback-close"
              onClick={closeFeedbackReport}
              aria-label="Close report dialog"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
            <div className="app-dialog-head">
              <h4 className="app-dialog-title">Think Our Evaluator is Incorrect?</h4>
            </div>
            <div className="app-confirm-content">
              <p>
                If something in this evaluation seems off, you can flag it here so we can review it more closely.
              </p>
            </div>
            <OarsFeedbackContext oarsAttempt={oarsAttempt} />
            <div className="app-feedback-field">
              <div className="app-feedback-field-header">
                <label className="app-feedback-label" htmlFor="report-feedback-input">Your Feedback</label>
                <span className="app-feedback-word-count">{`${feedbackReportWordCount} / 150 words`}</span>
              </div>
              <textarea
                id="report-feedback-input"
                className="app-feedback-input"
                value={feedbackReportText}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const nextCount = nextValue.trim()
                    ? nextValue.trim().split(/\s+/).filter(Boolean).length
                    : 0;
                  if (nextCount <= 150) {
                    setFeedbackReportText(nextValue);
                  }
                }}
                rows={4}
                placeholder="Share your thoughts here..."
              />
            </div>
            <div className="app-dialog-actions app-feedback-actions">
              <button
                type="button"
                className="app-confirm-btn primary"
                onClick={handleFeedbackReportSubmit}
                disabled={feedbackReportSubmitting}
              >
                {feedbackReportSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!actionDockDetached && (
        <OarsActionDock
          show={showOarsActionDock}
          t={t}
          onOarsTryAgain={onOarsTryAgain}
          onOarsNextExercise={onOarsNextExercise}
          onReturnToAccount={onReturnToAccount}
          isSpecificSkillReplay={isSpecificSkillReplay}
          showSpecificSkillsPostAttemptCue={showSpecificSkillsPostAttemptCue}
        />
      )}
    </>
  );
}

export { OarsActionDock };
export default ReportOarsPanel;
